/**
 * Scheduling Credential Setup Panel — Admin Dashboard
 *
 * Markets list with scheduler state, Connect/Disconnect Google Calendar,
 * calendar picker, availability windows editor, test connection.
 *
 * API Endpoints:
 * - GET    /api/v1/admin/scheduling/state                       — Markets with scheduler state
 * - GET    /api/v1/admin/scheduling/connect                     — OAuth URL
 * - GET    /api/v1/admin/scheduling/callback?code=...&state=... — OAuth callback
 * - PATCH  /api/v1/admin/scheduling/credentials/:id/calendar    — Set calendar
 * - PATCH  /api/v1/admin/scheduling/credentials/:id/availability — Edit availability
 * - PATCH  /api/v1/admin/scheduling/markets/:marketId/scheduler — Assign scheduler to market
 * - DELETE /api/v1/admin/scheduling/credentials/:id             — Disconnect
 * - POST   /api/v1/admin/scheduling/credentials/:id/test        — Test connection
 *
 * @story SCRUM-1472 — 0.1.1.10-04: Scheduling Credential Setup Panel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiRequest from '../../services/api';

// ─── Styles ────────────────────────────────────────────────────────

const styles = {
  container: { maxWidth: '1200px', margin: '0 auto', padding: '24px' },
  title: { fontSize: '24px', fontWeight: '600', margin: '0 0 24px' },
  card: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '16px' },
  marketName: { fontSize: '16px', fontWeight: '600', marginBottom: '4px' },
  subText: { fontSize: '13px', color: '#6b7280', marginBottom: '8px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  actions: { display: 'flex', gap: '8px', flexShrink: 0 },
  btn: { padding: '6px 14px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '13px' },
  btnPrimary: { backgroundColor: '#3b82f6', color: '#fff' },
  btnDanger: { backgroundColor: '#fee2e2', color: '#991b1b' },
  btnSecondary: { backgroundColor: '#f3f4f6', color: '#374151' },
  btnSuccess: { backgroundColor: '#10b981', color: '#fff' },
  btnDisabled: { backgroundColor: '#e5e7eb', color: '#9ca3af', cursor: 'not-allowed' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600' },
  badgeConnected: { backgroundColor: '#dcfce7', color: '#166534' },
  badgeDisconnected: { backgroundColor: '#f3f4f6', color: '#6b7280' },
  fieldRow: { display: 'flex', gap: '12px', marginBottom: '8px', alignItems: 'center' },
  label: { fontWeight: '500', fontSize: '13px', color: '#6b7280', width: '120px', flexShrink: 0 },
  value: { fontSize: '14px', color: '#111827' },
  input: { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' },
  select: { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '520px', maxHeight: '90vh', overflowY: 'auto' },
  testSuccess: { color: '#166534', fontSize: '13px', marginTop: '4px' },
  testError: { color: '#991b1b', fontSize: '13px', marginTop: '4px' },
  windowRow: { display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' },
  dayChip: { padding: '2px 8px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', border: '1px solid #d1d5db' },
  dayChipActive: { backgroundColor: '#3b82f6', color: '#fff', borderColor: '#3b82f6' },
};

const TIMEZONES = [
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Los_Angeles',
  'America/Phoenix',
];

const SLOT_DURATIONS = [15, 30, 45, 60];

const DAY_LABELS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── Component ─────────────────────────────────────────────────────

export default function SchedulingPanel() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [calendarPickerState, setCalendarPickerState] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const fetchState = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest('/api/v1/admin/scheduling/state');
      if (res.success) {
        setMarkets(res.data);
      }
    } catch {
      // handled by apiRequest
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchState(); }, [fetchState]);

  const handleConnect = async (marketId) => {
    try {
      const res = await apiRequest('/api/v1/admin/scheduling/connect');
      if (res.url) {
        sessionStorage.setItem('scheduling_connect_market_id', marketId);
        window.open(res.url, '_blank', 'width=600,height=700');
      }
    } catch (err) {
      showToast(err.message || 'Failed to start OAuth connection', 'error');
    }
  };

  // Listen for OAuth callback message from popup (same-origin postMessage)
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) { return; }
      if (event.data?.type !== 'scheduling_oauth_complete') { return; }

      const marketId = sessionStorage.getItem('scheduling_connect_market_id');
      sessionStorage.removeItem('scheduling_connect_market_id');

      if (event.data.success === false) {
        showToast(event.data.error || 'Calendar connection failed', 'error');
        await fetchState();
        return;
      }

      const { credential_id, calendars } = event.data;
      if (calendars && calendars.length > 0) {
        setCalendarPickerState({ credentialId: credential_id, calendars, marketId });
      } else {
        await fetchState();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchState]);

  const handleCalendarSelect = async (credentialId, calendarId, calendarTimezone, marketId) => {
    try {
      await apiRequest(`/api/v1/admin/scheduling/credentials/${credentialId}/calendar`, {
        method: 'PATCH',
        body: JSON.stringify({ calendar_id: calendarId, timezone: calendarTimezone || undefined }),
      });

      if (marketId) {
        await apiRequest(`/api/v1/admin/scheduling/markets/${marketId}/scheduler`, {
          method: 'PATCH',
          body: JSON.stringify({ scheduler_id: credentialId }),
        });
      }

      setCalendarPickerState(null);
      showToast('Calendar saved');
      await fetchState();
    } catch (err) {
      showToast(err.message || 'Failed to save calendar selection', 'error');
    }
  };

  const handleDisconnect = async (credentialId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Disconnect this Google Calendar? Availability slots will no longer be generated for this market.')) {
      return;
    }
    try {
      await apiRequest(`/api/v1/admin/scheduling/credentials/${credentialId}`, {
        method: 'DELETE',
      });
      await fetchState();
    } catch (err) {
      showToast(err.message || 'Failed to disconnect calendar', 'error');
    }
  };

  const handleTestConnection = async (credentialId) => {
    setTestResults((prev) => ({ ...prev, [credentialId]: { loading: true } }));
    try {
      const res = await apiRequest(`/api/v1/admin/scheduling/credentials/${credentialId}/test`, {
        method: 'POST',
      });
      setTestResults((prev) => ({ ...prev, [credentialId]: res }));
    } catch (err) {
      setTestResults((prev) => ({ ...prev, [credentialId]: { success: false, error: err.message || 'Test failed' } }));
    }
  };

  const formatWindows = (windows) => {
    if (!windows || windows.length === 0) { return 'No availability set'; }
    return windows.map((w) => {
      const days = w.days.map((d) => DAY_LABELS[d]).join(', ');
      return `${days} ${w.start} – ${w.end}`;
    }).join(' | ');
  };

  const layoutProps = {
    userRole: 'admin',
    activeNavId: 'scheduling',
    onNavigate: handleNavigate,
    user: {
      name: authUser?.fullName || authUser?.firstName || 'Admin',
      email: authUser?.email || '',
      avatar: authUser?.profilePicture || null
    }
  };

  if (loading) {
    return <DashboardLayout {...layoutProps}><div style={styles.container}><p>Loading scheduling state...</p></div></DashboardLayout>;
  }

  return (
    <DashboardLayout {...layoutProps}>
    <div style={styles.container}>
      <h1 style={styles.title}>Scheduling Setup</h1>

      {markets.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No markets found.</p>
      ) : (
        markets.map((m) => {
          const isConnected = m.scheduler_id && m.scheduler_active;
          const test = testResults[m.scheduler_id];

          return (
            <div key={m.market_id} style={styles.card}>
              <div style={styles.row}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={styles.marketName}>{m.market_name}</span>
                    <span style={{ ...styles.badge, ...(isConnected ? styles.badgeConnected : styles.badgeDisconnected) }}>
                      {isConnected ? 'Connected' : 'Not connected'}
                    </span>
                  </div>

                  {isConnected ? (
                    <>
                      <p style={styles.subText}>{m.scheduler_email}</p>
                      <div style={styles.fieldRow}>
                        <span style={styles.label}>Calendar</span>
                        <span style={styles.value}>{m.calendar_id === 'primary' ? 'Primary' : m.calendar_id}</span>
                      </div>
                      <div style={styles.fieldRow}>
                        <span style={styles.label}>Availability</span>
                        <span style={styles.value}>{formatWindows(m.availability_windows)}</span>
                      </div>
                      <div style={styles.fieldRow}>
                        <span style={styles.label}>Timezone</span>
                        <span style={styles.value}>{m.timezone}</span>
                      </div>
                      <div style={styles.fieldRow}>
                        <span style={styles.label}>Slot Duration</span>
                        <span style={styles.value}>{m.slot_duration_mins} min</span>
                      </div>

                      {test && !test.loading && (
                        <p style={test.success ? styles.testSuccess : styles.testError}>
                          {test.success
                            ? `Connected — ${test.calendarCount} calendar(s) found`
                            : `Failed: ${test.error}`}
                        </p>
                      )}
                    </>
                  ) : (
                    <p style={styles.subText}>No Google Calendar connected</p>
                  )}
                </div>

                <div style={styles.actions}>
                  {isConnected ? (
                    <>
                      <button
                        style={{ ...styles.btn, ...styles.btnSecondary }}
                        onClick={() => setEditingId(m.scheduler_id)}
                      >
                        Edit
                      </button>
                      <button
                        style={{ ...styles.btn, ...styles.btnPrimary }}
                        onClick={() => handleTestConnection(m.scheduler_id)}
                        disabled={test?.loading}
                      >
                        {test?.loading ? 'Testing...' : 'Test Connection'}
                      </button>
                      <button
                        style={{ ...styles.btn, ...styles.btnDanger }}
                        onClick={() => handleDisconnect(m.scheduler_id)}
                      >
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button
                      style={{ ...styles.btn, ...styles.btnSuccess }}
                      onClick={() => handleConnect(m.market_id)}
                    >
                      Connect Google Calendar
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}

      {calendarPickerState && (
        <CalendarPickerModal
          calendars={calendarPickerState.calendars}
          onSelect={(calendarId, calendarTimezone) => handleCalendarSelect(
            calendarPickerState.credentialId,
            calendarId,
            calendarTimezone,
            calendarPickerState.marketId
          )}
          onClose={() => { setCalendarPickerState(null); fetchState(); }}
        />
      )}

      {editingId && (
        <AvailabilityEditModal
          credentialId={editingId}
          market={markets.find((m) => m.scheduler_id === editingId)}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); showToast('Availability saved'); fetchState(); }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 1100,
          padding: '12px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
          color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          backgroundColor: toast.type === 'error' ? '#ef4444' : '#10b981',
        }}>
          {toast.message}
        </div>
      )}
    </div>
    </DashboardLayout>
  );
}

// ─── Calendar Picker Modal ─────────────────────────────────────────

function CalendarPickerModal({ calendars, onSelect, onClose }) {
  const [selected, setSelected] = useState(calendars[0]?.id || '');

  const handleConfirm = () => {
    const cal = calendars.find((c) => c.id === selected);
    onSelect(selected, cal?.timeZone || null);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: '18px' }}>Select Calendar</h2>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
          Choose which calendar to use for scheduling availability.
        </p>
        <select
          style={{ ...styles.select, width: '100%', marginBottom: '16px' }}
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          {calendars.map((c) => (
            <option key={c.id} value={c.id}>
              {c.summary || c.id}{c.timeZone ? ` (${c.timeZone})` : ''}
            </option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={onClose}>Cancel</button>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ─── Availability Edit Modal ───────────────────────────────────────

function AvailabilityEditModal({ credentialId, market, onClose, onSaved, onError }) {
  const [windows, setWindows] = useState(market?.availability_windows || [{ start: '09:00', end: '17:00', days: [1, 2, 3, 4, 5] }]);
  const [timezone, setTimezone] = useState(market?.timezone || 'America/Denver');
  const [slotDuration, setSlotDuration] = useState(market?.slot_duration_mins || 30);
  const [saving, setSaving] = useState(false);

  const toggleDay = (windowIdx, day) => {
    setWindows((prev) => {
      const updated = [...prev];
      const w = { ...updated[windowIdx] };
      if (w.days.includes(day)) {
        w.days = w.days.filter((d) => d !== day);
      } else {
        w.days = [...w.days, day].sort();
      }
      updated[windowIdx] = w;
      return updated;
    });
  };

  const updateWindow = (windowIdx, field, value) => {
    setWindows((prev) => {
      const updated = [...prev];
      updated[windowIdx] = { ...updated[windowIdx], [field]: value };
      return updated;
    });
  };

  const addWindow = () => {
    setWindows((prev) => [...prev, { start: '09:00', end: '17:00', days: [1, 2, 3, 4, 5] }]);
  };

  const removeWindow = (idx) => {
    setWindows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiRequest(`/api/v1/admin/scheduling/credentials/${credentialId}/availability`, {
        method: 'PATCH',
        body: JSON.stringify({
          availability_windows: windows,
          timezone,
          slot_duration_mins: slotDuration,
        }),
      });
      onSaved();
    } catch (err) {
      if (onError) { onError(err.message || 'Failed to save availability'); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: '18px' }}>Edit Availability — {market?.market_name}</h2>

        {windows.map((w, idx) => (
          <div key={idx} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>Window {idx + 1}</span>
              {windows.length > 1 && (
                <button
                  style={{ ...styles.btn, ...styles.btnDanger, padding: '2px 8px', fontSize: '11px' }}
                  onClick={() => removeWindow(idx)}
                >
                  Remove
                </button>
              )}
            </div>

            <div style={styles.windowRow}>
              {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                <span
                  key={day}
                  style={{ ...styles.dayChip, ...(w.days.includes(day) ? styles.dayChipActive : {}) }}
                  onClick={() => toggleDay(idx, day)}
                >
                  {DAY_LABELS[day]}
                </span>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="time"
                style={styles.input}
                value={w.start}
                onChange={(e) => updateWindow(idx, 'start', e.target.value)}
              />
              <span style={{ color: '#6b7280' }}>to</span>
              <input
                type="time"
                style={styles.input}
                value={w.end}
                onChange={(e) => updateWindow(idx, 'end', e.target.value)}
              />
            </div>
          </div>
        ))}

        <button style={{ ...styles.btn, ...styles.btnSecondary, marginBottom: '16px' }} onClick={addWindow}>
          + Add Window
        </button>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ ...styles.label, display: 'block', marginBottom: '4px' }}>Timezone</label>
          <select style={{ ...styles.select, width: '100%' }} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ ...styles.label, display: 'block', marginBottom: '4px' }}>Slot Duration</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {SLOT_DURATIONS.map((d) => (
              <label key={d} style={{ fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="slotDuration"
                  value={d}
                  checked={slotDuration === d}
                  onChange={() => setSlotDuration(d)}
                />{' '}
                {d} min
              </label>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={onClose}>Cancel</button>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
